import { useMemo } from 'react';
import { useUser } from '@r/api/auth.tsx';
import shopDefaultBanner from '@r/assets/images/fiddy/default-shop-banner.png';
import { useTranslation } from 'react-i18next';
import Marquee from '@ui/marquee.tsx';
import { Skeleton } from '@ui/skeleton.tsx';
import {
  useApiShopsGetCollection,
  useApiWidgetWorkspacesGetCollection
} from '@r/api/generated/client.ts';
import { isShopWidgetKey } from '@r/helpers/widget-workspace-slides.ts';
import WidgetHome from '@client/components/widget/widget-home.tsx';
import type {
  ShopShopReadModel,
  ShopWorkspaceShopReadModel
} from '@r/api/generated/client.schemas.ts';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { useBalance } from '@r/context/balance-context.ts';
import { ScaleCtaCard } from '@client/components/games/scale-cta-card.tsx';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { PageContainer } from '@client/components/layout/page-container.tsx';
import { Navigate } from '@tanstack/react-router';
import { Loader } from '@r/components/loader/loader.tsx';

type Shop = ShopShopReadModel;
type ShopWorkspace = ShopWorkspaceShopReadModel;
type ShopWithAccess = Shop & {
  accessStatus: boolean;
  accessMessage: string;
};

function isShopOpen(shopSettings: ShopWorkspace | null | undefined): boolean {
  if (!shopSettings) {
    return false;
  }

  const now = new Date();
  const shopOpeningDate = shopSettings.shopOpeningDate
    ? new Date(shopSettings.shopOpeningDate)
    : null;
  const shopClosingDate = shopSettings.shopClosingDate
    ? new Date(shopSettings.shopClosingDate)
    : null;

  if (!shopOpeningDate && !shopClosingDate) {
    // No opening or closing date specified.
    return true;
  }

  if (shopOpeningDate && !shopClosingDate) {
    // Only opening date specified.
    return now >= shopOpeningDate;
  }

  if (!shopOpeningDate && shopClosingDate) {
    // Only closing date specified
    return now <= shopClosingDate;
  }

  if (shopOpeningDate && shopClosingDate) {
    // Both opening and closing dates specified
    return now >= shopOpeningDate && now <= shopClosingDate;
  }

  return false;
}

function hasEnoughPoints(balance: number, minPoint: number): boolean {
  return balance >= minPoint;
}

const Shops = () => {
  const { t } = useTranslation();
  const user = useUser().data;
  const workspaces = UseWorkspace(user);
  const { balance } = useBalance();

  const widgetWorkspacesQuery = useApiWidgetWorkspacesGetCollection({
    query: {
      queryKey: ['widget-workspaces'],
      staleTime: 5 * 60 * 1000
    }
  });
  useOrvalQueryErrorToast(widgetWorkspacesQuery, t('toast.error.label'));

  const activeShopWidgetWorkspaces = useMemo(
    () =>
      (widgetWorkspacesQuery.data ?? []).filter(
        (workspace) => workspace.isActive && isShopWidgetKey(workspace.widget?.key)
      ),
    [widgetWorkspacesQuery.data]
  );

  let shopsList: ShopWithAccess[] = [];

  const singleActiveShop =
    workspaces.workspace?.activeShops?.length === 1
      ? workspaces.workspace.activeShops[0]
      : undefined;
  const isDataAvailable = !!workspaces.workspace?.id && !!user?.id && !singleActiveShop;

  const shopsQuery = useApiShopsGetCollection(undefined, {
    query: {
      queryKey: ['shops', workspaces.workspace?.id, user?.id],
      staleTime: Number.POSITIVE_INFINITY,
      enabled: isDataAvailable
    }
  });
  useOrvalQueryErrorToast(shopsQuery, t('toast.error.label'));

  const {
    data: shops,
    isPending: isShopsPending,
    isError: isShopsError,
    isSuccess: isShopsSuccess
  } = shopsQuery;

  function addShopAccessStatus(shop: Shop): ShopWithAccess {
    const shopSettings = shop.shopWorkspace;

    if (!shopSettings || !shopSettings.isActive) {
      return {
        ...shop,
        accessStatus: false,
        accessMessage: t('shop.error.not.available')
      };
    }

    const minPoint = shopSettings?.minPoint ?? 0;
    const isShopOpenStatus = isShopOpen(shopSettings);
    const hasAccess = hasEnoughPoints(balance, minPoint) && isShopOpenStatus;
    const accessMessage = hasAccess
      ? t('shop.access.granted')
      : `${t('shop.access.denied')} ${!isShopOpenStatus ? t('shop.error.closed') : t('shop.error.insufficient.points')}`;

    return {
      ...shop,
      accessStatus: hasAccess,
      accessMessage: accessMessage
    };
  }

  if (isShopsSuccess && shops?.data) {
    shopsList = shops.data.map((shop: Shop) => addShopAccessStatus(shop)) || [];
  }

  if (singleActiveShop) {
    return (
      <Navigate
        to="/shops/$shopId/products"
        params={{ shopId: singleActiveShop.id.toString() }}
        replace
      />
    );
  }

  if (workspaces.isWorkspacePending) {
    return <Loader />;
  }

  return (
    <>
      <PageContainer className="pt-10 pb-7 space-y-5">
        {activeShopWidgetWorkspaces.length > 0 && (
          <WidgetHome widgetWorkspaces={activeShopWidgetWorkspaces} />
        )}

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {(isShopsPending || isShopsError) && (
            <Skeleton className="flex relative flex-col justify-center items-center space-y-6 h-60 rounded-3xl bg-muted-foreground/40">
              <Skeleton className="w-3/6 h-10" />
              <Skeleton className="absolute bottom-2.5 right-2.5 w-10 aspect-square rounded-full" />
            </Skeleton>
          )}

          {isShopsSuccess &&
            shopsList.length > 0 &&
            shopsList.map((shop: ShopWithAccess) =>
              shop.accessStatus ? (
                <ScaleCtaCard
                  key={shop.id}
                  to="/shops/$shopId/products"
                  params={{ shopId: shop.id.toString() }}
                  imageSrc={shop.banner?.pathUri || shopDefaultBanner}
                  imageAlt={`${shop.displayName} banner`}
                  title={shop.displayName}
                  ctaLabel={t('shops.card.btn.label')}
                  defaultImage={shopDefaultBanner}
                />
              ) : (
                <ScaleCtaCard
                  key={shop.id}
                  imageSrc={shop.banner?.pathUri || shopDefaultBanner}
                  imageAlt={`${shop.displayName} banner`}
                  title={shop.displayName}
                  defaultImage={shopDefaultBanner}
                  unavailable={true}
                  bottomMessage={shop.accessMessage}
                />
              )
            )}
        </div>
      </PageContainer>

      <Marquee text={t('shops.animated.text')} />
    </>
  );
};

export { Shops };
